import React, {useState} from "react";
import {MenuItem, TextField, Typography} from "@mui/material";
import CCButton from "../Common/CCButton";
import css_buttons from "../Common/css/CCButton.module.scss";
import {Blue3, Grey2, Grey6, Grey7, Grey17} from "../../theme/styles";
import {DisplayItem, ShapeField} from "./types";

/**
 * What can be put on the canvas: the shape's unplaced fields, the display widgets, and a way to
 * invent a field the shape does not have.
 *
 * THE PALETTE IS WIDGETS, NOT TYPES, and the two halves are why. A palette of types alone cannot
 * express a heading or a block of instructions — those carry no data and create no field. A palette
 * of widgets alone cannot express "ask the question the catalogue already defines". So it is both:
 * the shape's fields, which arrive already knowing what they are, and widgets that a data type is
 * bound to at the moment of placing.
 *
 * A FIELD ALREADY PLACED IS NOT OFFERED. One question, asked once — the server refuses a duplicate
 * code anyway, and offering something that will be refused is worse than not offering it.
 */

const DISPLAY_WIDGETS: {kind: DisplayItem["kind"]; label: string; hint: string}[] = [
    {kind: "heading", label: "Heading", hint: "A title inside a section"},
    {kind: "instructions", label: "Instructions", hint: "A paragraph telling somebody what to do"},
    {kind: "divider", label: "Divider", hint: "A rule between groups of questions"},
];

type DataType = {USID?: string; Name?: string; Kind?: string; Code?: string};

type Props = {
    fields: ShapeField[];
    dataTypes: DataType[];
    disabled?: boolean;
    /** Where a click-to-add lands, since a click says nothing about which section. */
    targetSection: string | null;
    onPlace: (field: ShapeField) => void;
    onInvent: (code: string, name: string, dataTypeRevisionUSID: string) => void;
    onAddDisplay: (kind: DisplayItem["kind"]) => void;
    /** Where an invented field ends up. A form's reaches the shape at release; a record's stays
     *  on the record until promoted. Said by the caller, because the palette cannot know. */
    inventNote?: string;
};

export default function Palette({fields, dataTypes, disabled, targetSection, onPlace, onInvent,
                                 onAddDisplay, inventNote}: Props) {
    const [inventing, setInventing] = useState(false);
    const [code, setCode] = useState("");
    const [name, setName] = useState("");
    const [type, setType] = useState("");

    const canInvent = code.trim().length > 0 && name.trim().length > 0 && !!type;

    const invent = () => {
        onInvent(code.trim(), name.trim(), type);
        setCode(""); setName(""); setType(""); setInventing(false);
    };

    const dragField = (e: React.DragEvent, field: ShapeField) => {
        e.dataTransfer.setData("application/x-cres-palette-field", field.Code);
        e.dataTransfer.effectAllowed = "copy";
    };

    const dragWidget = (e: React.DragEvent, kind: string) => {
        e.dataTransfer.setData("application/x-cres-palette-display", kind);
        e.dataTransfer.effectAllowed = "copy";
    };

    return (
        <div style={{width: 280, flexShrink: 0, borderRight: `1px solid ${Grey2}`, paddingRight: 18,
                     overflowY: "auto"}}>
            {/* WHERE A CLICK WILL LAND, said rather than left to a tooltip. A palette that silently
                chose a section for you is how every field ended up in Section 1. */}
            {targetSection && (
                <Typography style={{fontSize: 11, color: Grey6, marginTop: 14, lineHeight: 1.5}}>
                    Clicking adds to <b style={{color: Grey17}}>{targetSection}</b>.
                    Select another section to change that, or drag instead.
                </Typography>
            )}

            <Label>Questions the shape defines</Label>
            {fields.length === 0 && (
                <Typography style={{fontSize: 13, color: Grey7, marginBottom: 18}}>
                    Every field this shape defines is already on the form.
                </Typography>
            )}
            {fields.map(field => (
                <div
                    key={field.Code}
                    draggable={!disabled}
                    onDragStart={e => dragField(e, field)}
                    onClick={() => !disabled && onPlace(field)}
                    title={disabled ? undefined : `Add to ${targetSection ?? "the form"}`}
                    style={item(disabled)}
                >
                    <Typography style={{fontSize: 13, color: Grey17, fontWeight: 500}}>{field.Name}</Typography>
                    <Typography style={{fontSize: 11, color: Grey6}}>
                        {field.Code}{field.TypeName ? ` · ${field.TypeName}` : ""}
                        {field.Retired ? " · retired" : ""}
                    </Typography>
                </div>
            ))}

            <Label>Layout</Label>
            {DISPLAY_WIDGETS.map(widget => (
                <div
                    key={widget.kind}
                    draggable={!disabled}
                    onDragStart={e => dragWidget(e, widget.kind)}
                    onClick={() => !disabled && onAddDisplay(widget.kind)}
                    style={item(disabled)}
                >
                    <Typography style={{fontSize: 13, color: Grey17, fontWeight: 500}}>{widget.label}</Typography>
                    <Typography style={{fontSize: 11, color: Grey6}}>{widget.hint}</Typography>
                </div>
            ))}

            <Label>A question the shape does not have</Label>
            {!inventing && (
                <CCButton className={css_buttons.whiteStandardButton} disabled={disabled}
                          onClick={() => setInventing(true)}>New field</CCButton>
            )}
            {inventing && (
                <div style={{display: "flex", flexDirection: "column", gap: 10, marginTop: 6}}>
                    <TextField label="Code" size="small" value={code}
                               onChange={e => setCode(e.target.value)}
                               helperText="Permanent. What answers are stored under."/>
                    <TextField label="Label" size="small" value={name}
                               onChange={e => setName(e.target.value)}/>
                    <TextField select label="Type" size="small" value={type}
                               onChange={e => setType(e.target.value)}>
                        {dataTypes.map(t => (
                            <MenuItem key={t.USID} value={t.USID}>
                                {t.Name}{t.Kind ? ` · ${t.Kind}` : ""}
                            </MenuItem>
                        ))}
                    </TextField>
                    {/* Said plainly, because it is the one thing about inventing a field that is not
                        obvious: it does not exist in the catalogue until this form is released. */}
                    <Typography style={{fontSize: 11, color: Grey6}}>
                        {inventNote ?? "Added to the shape when this form is released, on a draft somebody then releases."}
                    </Typography>
                    <div style={{display: "flex", gap: 8}}>
                        <CCButton className={css_buttons.blueStandardButton} disabled={!canInvent}
                                  onClick={invent}>Add</CCButton>
                        <CCButton className={css_buttons.whiteStandardButton}
                                  onClick={() => setInventing(false)}>Cancel</CCButton>
                    </div>
                </div>
            )}
        </div>
    );
}

function Label({children}: {children: React.ReactNode}) {
    return (
        <Typography style={{fontSize: 11, fontWeight: 700, color: Grey6, textTransform: "uppercase",
                            letterSpacing: "0.5px", margin: "18px 0 8px"}}>
            {children}
        </Typography>
    );
}

const item = (disabled?: boolean): React.CSSProperties => ({
    border: `1px solid ${Grey2}`,
    borderLeft: `3px solid ${Blue3}`,
    borderRadius: 3,
    padding: "7px 10px",
    marginBottom: 6,
    cursor: disabled ? "default" : "grab",
    opacity: disabled ? 0.55 : 1,
    background: "#fff",
});
